
import React, { useState, useEffect } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaMapMarkerAlt,
  FaRegCalendarAlt,
  FaUserFriends,
  FaDollarSign,
  FaCloud,
  FaHome,
  FaBoxOpen,
  FaHeart,
  FaCheckCircle,
  FaExclamationCircle,
  FaMagic,
  FaRegCommentDots,
  FaSave,
  FaTrash,
  FaCalendarPlus,
  FaFileAlt
} from "react-icons/fa";
import { GroqDrawerTrigger } from "../components/GroqDrawerTrigger";
import { PaywallModal } from "../components/PaywallModal";
import { useSubscription } from "../hooks/useSubscription";
import { useEmbeddedPage } from "../hooks/useEmbeddedPage";
import { checkAIRequestLimit } from "../services/aiRequestService";
import { supabase } from "../services/supabase";

const TripPlannerPage = () => {
  const { handleBack } = useEmbeddedPage();
  const { tier, isPremium } = useSubscription();
  const [expandedSections, setExpandedSections] = useState({});
  const [completedItems, setCompletedItems] = useState({});
  const [tripName, setTripName] = useState("");
  const [itemDetails, setItemDetails] = useState({});
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState("");
  const [limitInfo, setLimitInfo] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [currentFieldForResponse, setCurrentFieldForResponse] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planContent, setPlanContent] = useState("");
  
  // State and refs for GroqDrawerTrigger
  const [drawerOpen, setDrawerOpen] = useState(false);
  const groqDrawerOpenRef = React.useRef(null);
  const groqSetQueryRef = React.useRef(null);
  
  // Load trip data from Supabase on component mount
  useEffect(() => {
    loadTripData();
  }, []);

  // Save trip data to Supabase
  const saveTripData = async () => {
    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert("Please log in to save your trip plan");
        setIsSaving(false);
        return;
      }

      const tripData = {
        trip_name: tripName,
        completed_items: completedItems,
        expanded_sections: expandedSections,
        item_details: itemDetails,
        last_updated: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("user_settings")
        .upsert([{ user_id: user.id, trip_data: tripData }], { onConflict: "user_id" });

      if (error) throw error;
      setLastSaved(new Date().toLocaleTimeString());
      setTimeout(() => setLastSaved(null), 3000);
    } catch (error) {
      console.error("Error saving trip data:", error);
      alert("Failed to save trip plan");
    } finally {
      setIsSaving(false);
    }
  };

  // Load trip data from Supabase
  const loadTripData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("user_settings")
        .select("trip_data")
        .eq("user_id", user.id)
        .single();

      if (error) {
        console.log("No saved trip data found");
        return;
      }

      if (data && data.trip_data) {
        setTripName(data.trip_data.trip_name || "");
        setCompletedItems(data.trip_data.completed_items || {});
        setExpandedSections(data.trip_data.expanded_sections || {});
        setItemDetails(data.trip_data.item_details || {});
      }
    } catch (error) {
      console.error("Error loading trip data:", error);
    }
  };

  // Clear all trip data
  const clearTripData = async () => {
    if (window.confirm("Are you sure? This will clear all your trip plan data.")) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Clear from Supabase
          await supabase
            .from("user_settings")
            .update({ trip_data: null })
            .eq("user_id", user.id);
        }
        
        // Clear local state
        setTripName("");
        setCompletedItems({});
        setExpandedSections({});
        setItemDetails({});
        setLastSaved(null);
        alert("Trip plan cleared successfully!");
      } catch (error) {
        console.error("Error clearing trip data:", error);
        alert("Failed to clear trip data. Please try again.");
      }
    }
  };

  // Create Google Calendar event
  const createGoogleCalendarEvent = () => {
    setIsCreatingEvent(true);
    const destination = itemDetails.destination || "Trip";
    
    // Format the date properly for Google Calendar
    let startDateFormatted;
    if (itemDetails.dates) {
      // Convert from YYYY-MM-DD to YYYYMMDD format for Google Calendar
      startDateFormatted = itemDetails.dates.replace(/-/g, "");
    } else {
      // Fall back to today's date in YYYYMMDD format
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      startDateFormatted = year + month + day;
    }
    
    const title = `${destination} - ${tripName || "Outdoor Trip"}`;
    const description = `Budget: ${itemDetails.budget || "Not set"}\nTravelers: ${itemDetails.travelers || "Not set"}\n\nAttractions & Activities:\n${itemDetails.attractions || "Not set"}\n\nSeasonal Conditions:\n${itemDetails.seasonal || "Not set"}`;
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startDateFormatted}/${startDateFormatted}&details=${encodeURIComponent(description)}&openui=1`;
    
    window.open(googleCalendarUrl, "_blank");
    setIsCreatingEvent(false);
  };

  // Download trip plan as text file
  const downloadTripPlanAsText = () => {
    const subject = `Trip Plan: ${tripName || "Outdoor Adventure"}`;
    const lines = [
      subject,
      '='.repeat(subject.length),
      '',
      `Budget: ${itemDetails.budget || "Not set"}`,
      `Travelers: ${itemDetails.travelers || "Not set"}`,
      '',
      `Attractions & Activities:`,
      `${itemDetails.attractions || "Not set"}`,
      '',
      `Seasonal Conditions:`,
      `${itemDetails.seasonal || "Not set"}`,
      '',
      `Day-by-Day Itinerary:`,
      `${itemDetails["daily-plan"] || "Not set"}`,
      '',
      `Backup Plans:`,
      `${itemDetails.backup || "Not set"}`,
      '',
      `Budget Estimate:`,
      `${itemDetails.estimate || "Not set"}`,
      '',
      `Packing List:`,
      `${itemDetails["packing-list"] || "Not set"}`,
      '',
      `Clothing & Gear:`,
      `${itemDetails.clothing || "Not set"}`,
      '',
      `Personal Items:`,
      `${itemDetails.personal || "Not set"}`
    ];
    const body = lines.join('\n');
    
    const element = document.createElement("a");
    element.setAttribute("href", "data:text/plain;charset=utf-8," + encodeURIComponent(body));
    element.setAttribute("download", `${tripName || "trip-plan"}.txt`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Show trip plan in modal
  const showTripPlan = () => {
    const subject = `Trip Plan: ${tripName || "Outdoor Adventure"}`;
    const lines = [
      subject,
      '='.repeat(subject.length),
      '',
      `Budget: ${itemDetails.budget || "Not set"}`,
      `Travelers: ${itemDetails.travelers || "Not set"}`,
      '',
      `Attractions & Activities:`,
      `${itemDetails.attractions || "Not set"}`,
      '',
      `Seasonal Conditions:`,
      `${itemDetails.seasonal || "Not set"}`,
      '',
      `Day-by-Day Itinerary:`,
      `${itemDetails["daily-plan"] || "Not set"}`,
      '',
      `Backup Plans:`,
      `${itemDetails.backup || "Not set"}`,
      '',
      `Budget Estimate:`,
      `${itemDetails.estimate || "Not set"}`,
      '',
      `Packing List:`,
      `${itemDetails["packing-list"] || "Not set"}`,
      '',
      `Clothing & Gear:`,
      `${itemDetails.clothing || "Not set"}`,
      '',
      `Personal Items:`,
      `${itemDetails.personal || "Not set"}`
    ];
    const body = lines.join('\n');
    
    setPlanContent(body);
    setShowPlanModal(true);
  };

  // Handle AI button clicks with paywall and limit check
  const handleAIButtonClick = async (question, itemId) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setPaywallFeature("Please log in to use Ask YOGi");
      setShowPaywall(true);
      return;
    }

    if (!isPremium) {
      // Check daily limit for free users
      const limitData = await checkAIRequestLimit(user.id, tier);
      
      if (!limitData.allowed) {
        setLimitInfo(limitData);
        setPaywallFeature("Daily Limit Reached");
        setShowPaywall(true);
        return;
      }

      // Warn if getting close to limit
      if (limitData.remaining === 1) {
        alert(`⚠️ You have ${limitData.remaining} Ask YOGi request left today`);
      }
    }

    // Original AI logic
    // Note: logAIRequest is called in GroqDrawer component when request is made
    setCurrentFieldForResponse(itemId);
    if (groqSetQueryRef.current && typeof groqSetQueryRef.current === "function") {
      groqSetQueryRef.current(question);
    }
    if (groqDrawerOpenRef.current) {
      groqDrawerOpenRef.current();
    } else {
      setDrawerOpen(true);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const toggleItem = (itemId) => {
    setCompletedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const sections = [
    {
      id: "basics",
      title: "1. Set the Basics",
      icon: FaMapMarkerAlt,
      color: "bg-blue-500",
      items: [
        { id: "destination", text: "Choose destination", field: "input" },
        { id: "dates", text: "Choose dates / length of trip", field: "date" },
        { id: "budget", text: "Set budget", field: "number" },
        { id: "travelers", text: "Decide who's going", field: "input" },
      ],
    },
    {
      id: "research",
      title: "2. Research",
      icon: FaCloud,
      color: "bg-emerald-500",
      items: [
        { id: "attractions", text: "Look up things to do", field: "textarea", question: "Suggest Top Outdoor Activities and Attractions for this destination" },
        { id: "seasonal", text: "Check weather / seasonal conditions", field: "textarea", question: "Provide seasonal conditions (weather, crowds, closures)." },
        { id: "safety", text: "Check safety or travel advisories" },
        { id: "neighborhoods", text: "Compare neighborhoods / areas to stay" },
        { id: "transport-options", text: "Look up transportation options" },
      ],
    },
    {
      id: "bookings",
      title: "3. Book Essentials",
      icon: FaRegCalendarAlt,
      color: "bg-purple-500",
      items: [
        { id: "lodging", text: "Book lodging", question: "Suggest lodging options for this destination" },
        { id: "transport", text: "Book transportation (flights, train, car, etc.)", question: "Suggest transportation options for this destination" },
        { id: "activities", text: "Book activities or tours", question: "Suggest top activities or tours to book for this destination." },
        { id: "permits", text: "Reserve any required permits", question: "What permits or reservations are required for this destination and how do I get them?" },
      ],
    },
    {
      id: "documents",
      title: "4. Documents & Requirements",
      icon: FaExclamationCircle,
      color: "bg-orange-500",
      items: [
        { id: "passport", text: "Check ID / passport validity" },
        { id: "visa", text: "Apply for visas (if needed)" },
        { id: "permits-docs", text: "Get permits/licenses" },
        { id: "insurance", text: "Buy travel or rental insurance" },
      ],
    },
    {
      id: "itinerary",
      title: "5. Build Your Itinerary",
      icon: FaMapMarkerAlt,
      color: "bg-cyan-500",
      items: [
        { id: "daily-plan", text: "Outline a flexible day-by-day plan", field: "textarea", question: "Create a detailed yet flexible day-by-day itinerary for this trip. Include main activities, travel times, meal suggestions, and rest periods. Make it flexible to accommodate weather changes or spontaneous activities." },
        { id: "map-locations", text: "Map key locations" },
        { id: "offline-maps", text: "Download offline maps" },
        { id: "backup", text: "Add backup options for weather or closures" },
      ],
    },
    {
      id: "money",
      title: "6. Budget & Money",
      icon: FaDollarSign,
      color: "bg-green-500",
      items: [
        { id: "estimate", text: "Estimate total costs", field: "textarea", question: "Provide a detailed cost breakdown for this trip including accommodation, meals, activities, transportation, gear rentals, and contingency. Include estimated costs per day and total budget." },
        { id: "track", text: "Track major reservations" },
        { id: "currency", text: "Set up payment methods (cards, currency, etc.)" },
      ],
    },
    {
      id: "packing",
      title: "7. Packing & Gear",
      icon: FaBoxOpen,
      color: "bg-amber-500",
      items: [
        { id: "packing-list", text: "Make a packing list", field: "textarea", question: "Create a comprehensive packing list for this trip based on the destination, duration, season, and planned activities. Include clothing, gear, toiletries, documents, and emergency items. Organize by category." },
        { id: "clothing", text: "Pack clothing for the weather/activities" },
        { id: "personal", text: "Prep personal items (meds, chargers, docs)" },
        { id: "test-gear", text: "Prepare or test gear" },
      ],
    },
    {
      id: "health",
      title: "8. Health & Safety",
      icon: FaHeart,
      color: "bg-red-500",
      items: [
        { id: "first-aid", text: "Pack first-aid items" },
        { id: "prescriptions", text: "Refill prescriptions" },
        { id: "health-req", text: "Check health/entry requirements" },
        { id: "share-itinerary", text: "Share itinerary + emergency contacts" },
      ],
    },
    {
      id: "home",
      title: "9. Home & Work Prep",
      icon: FaHome,
      color: "bg-indigo-500",
      items: [
        { id: "pet-care", text: "Arrange pet/house care" },
        { id: "work-notify", text: "Set out-of-office notifications" },
        { id: "bills", text: "Handle bills or tasks before leaving" },
      ],
    },
    {
      id: "final",
      title: "10. Tech & Final Steps",
      icon: FaCheckCircle,
      color: "bg-teal-500",
      items: [
        { id: "entertainment", text: "Download entertainment & apps" },
        { id: "confirmations", text: "Confirm all reservations" },
        { id: "checkin", text: "Check in for transportation" },
        { id: "weather", text: "Re-check weather" },
        { id: "charge", text: "Charge devices / pack last-minute items" },
      ],
    },
  ];

  const getProgress = () => {
    const total = sections.reduce((acc, section) => acc + section.items.length, 0);
    const completed = Object.values(completedItems).filter(Boolean).length;
    return Math.round((completed / total) * 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-4xl font-bold text-gray-800" style={{fontFamily: 'Times New Roman, Times, serif'}}>
              Outdoor Trip Planner
            </h1>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{getProgress()}%</div>
              <div className="text-sm text-gray-500">Complete</div>
            </div>
          </div>
          <input
            type="text"
            placeholder="Name your adventure..."
            value={tripName}
            onChange={(e) => setTripName(e.target.value)}
            className="w-full px-4 py-3 text-xl border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
          />
          {/* Progress Bar */}
          <div className="mt-6 bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-full transition-all duration-500"
              style={{ width: `${getProgress()}%` }}
            />
          </div>
          
          {/* Action Buttons */}
          <div className="mt-6 flex gap-3 flex-wrap">
            <button
              onClick={saveTripData}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              <FaSave size={16} /> {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={clearTripData}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium"
            >
              <FaTrash size={16} /> Clear
            </button>
            <button
              onClick={createGoogleCalendarEvent}
              disabled={isCreatingEvent}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              <FaCalendarPlus size={16} /> {isCreatingEvent ? "Opening..." : "Add to Calendar"}
            </button>
            <button
              onClick={showTripPlan}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
            >
              <FaRegCommentDots size={16} /> Show Plan
            </button>
            {lastSaved && (
              <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                ✓ Saved at {lastSaved}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-4">
          {sections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSections[section.id];
            const sectionCompleted = section.items.every((item) => completedItems[item.id]);
            const sectionProgress = section.items.filter((item) => completedItems[item.id]).length;
            return (
              <div key={section.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                {/* Section Header */}
                <div
                  onClick={() => toggleSection(section.id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`${section.color} p-3 rounded-lg text-white`}>
                      <Icon size={24} />
                    </div>
                    <div className="text-left">
                      <h2 className="text-xl font-semibold text-gray-800">{section.title}</h2>
                      <p className="text-sm text-gray-500">
                        {sectionProgress} of {section.items.length} complete
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {sectionCompleted && (
                      <FaCheckCircle className="text-green-500" size={24} />
                    )}
                    {/* AI Helper button for Research, Bookings, Itinerary, Money & Packing sections */}
                    {(section.id === "research" || section.id === "bookings" || section.id === "itinerary" || section.id === "money" || section.id === "packing") && (
                      <svg width="100" height="56" viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg">
                        <path d="M120 60 H680 Q720 60 715 100 L620 420 Q610 450 570 450 H230 Q190 450 180 420 L85 100 Q80 60 120 60 Z" fill="#5A3E36" stroke="#2A1F1B" strokeWidth="8" />
                        <path d="M145 95 H655 Q685 95 681 125 L598 405 Q590 430 555 430 H245 Q210 430 202 405 L119 125 Q115 95 145 95 Z" fill="none" stroke="#E8DCCB" strokeWidth="3" opacity="0.45" />
                        <text x="400" y="270" textAnchor="middle" fontFamily="Times New Roman, Times, serif" fontSize="90" fontWeight="700" fill="#ffffff">Ask YOGi</text>
                      </svg>
                    )}
                    {isExpanded ? <FaChevronDown size={24} /> : <FaChevronRight size={24} />}
                  </div>
                </div>
                {/* Section Items */}
                {isExpanded && (
                  <div className="px-6 pb-6 space-y-3 border-t">
                    {section.items.map((item) => (
                      <div key={item.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50">
                        <button
                          onClick={() => toggleItem(item.id)}
                          className={`mt-1 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            completedItems[item.id]
                              ? "bg-green-500 border-green-500"
                              : "border-gray-300 hover:border-green-400"
                          }`}
                        >
                          {completedItems[item.id] && (
                            <FaCheckCircle size={16} className="text-white" />
                          )}
                        </button>
                        <div className="flex-1">
                          <label
                            className={`cursor-pointer flex items-center gap-2 ${
                              completedItems[item.id]
                                ? "line-through text-gray-400"
                                : "text-gray-700"
                            }`}
                          >
                            {item.text}
                            {item.question && (
                              <button
                                onClick={() => handleAIButtonClick(item.question, item.id)}
                                className="hover:scale-125 transition-transform"
                                title="Ask Yogi"
                              >
                                <FaRegCommentDots size={20} style={{ color: '#5A3E36' }} />
                              </button>
                            )}
                          </label>
                          {/* Always show editable textarea for items with a question property */}
                          {item.question ? (
                            <textarea
                              className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                              placeholder={`Enter ${item.text.toLowerCase()}...`}
                              value={itemDetails[item.id] || ""}
                              onChange={(e) =>
                                setItemDetails((prev) => ({
                                  ...prev,
                                  [item.id]: e.target.value,
                                }))
                              }
                              rows={4}
                            />
                          ) : item.field ? (
                            <input
                              type={item.field}
                              className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                              placeholder={`Enter ${item.text.toLowerCase()}...`}
                              value={itemDetails[item.id] || ""}
                              onChange={(e) =>
                                setItemDetails((prev) => ({
                                  ...prev,
                                  [item.id]: e.target.value,
                                }))
                              }
                            />
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>Ready for adventure? Check off items as you complete them!</p>
        </div>
      </div>
      
      {/* GroqDrawerTrigger for AI assistance */}
      <GroqDrawerTrigger 
        openDrawer={drawerOpen} 
        onOpen={(fn) => { groqDrawerOpenRef.current = fn; }} 
        setQuery={(fn) => { groqSetQueryRef.current = fn; }}
        systemPromptKey="blm_map_system_prompt"
        onResponse={(response) => {
          if (currentFieldForResponse) {
            setItemDetails((prev) => ({
              ...prev,
              [currentFieldForResponse]: response,
            }));
            setCurrentFieldForResponse(null);
          }
        }}
      />

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => {
          setShowPaywall(false);
          setLimitInfo(null);
        }}
        feature={paywallFeature}
        limitInfo={limitInfo}
        onUpgrade={() => {
          // TODO: Redirect to Stripe checkout
          console.log("Redirect to upgrade");
          setShowPaywall(false);
          setLimitInfo(null);
        }}
      />

      {/* Trip Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Trip Plan Summary</h2>
              <button
                onClick={() => setShowPlanModal(false)}
                className="text-white hover:bg-blue-800 rounded-full p-1 transition"
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto flex-1 px-6 py-4">
              <pre className="font-sans text-sm text-gray-700 whitespace-pre-wrap break-words">
                {planContent}
              </pre>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-100 px-6 py-4 flex gap-3 justify-end border-t">
              <button
                onClick={() => setShowPlanModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-medium"
              >
                Close
              </button>
              <button
                onClick={downloadTripPlanAsText}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default TripPlannerPage;
